import {
	type QueryClient,
	queryOptions,
	type UseQueryResult,
	useMutation,
	useQueries,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { isNotNil } from 'es-toolkit'
import { max } from 'es-toolkit/compat'
import i18next from 'i18next'
import { HTTPError, type KyResponse, type NormalizedOptions } from 'ky'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { z } from 'zod'
import { createApiClient } from '../api/helpers.ts'
import { authManager } from '../auth-manager.ts'
import { env } from '../env.ts'
import type { DeviceMetric, DeviceMetricRecord } from './deviceMetrics.ts'
import { useImpersonation } from './impersonation.ts'
import { resetAllSockets, subscribeToSocket, unsubscribeFromSocket } from './websocketHandler.ts'

const deviceQueryKeys = {
	DEVICES: ['STREAM', 'DEVICES'],
	DEVICE_RECORD: ['STREAM', 'DEVICE_RECORD'],
	DEVICE_ACTIVITY: ['STREAM', 'DEVICE_ACTIVITY'],
	DEVICE_STATUS: ['STREAM', 'DEVICE_STATUS'],
	DEVICE_METRICS: ['STREAM', 'DEVICE_METRICS'],
}

class ErrorWithStatus extends HTTPError {
	status: number
	constructor(response: Response, request: Request, options: NormalizedOptions, status: number) {
		super(response, request, options)
		this.status = status
	}
}

const DevicesApi = createApiClient({
	prefixUrl: env.API_DEVICES,
	hooks: {
		beforeError: [
			(error) => {
				return new ErrorWithStatus(error.response, error.request, error.options, error.response.status)
			},
		],
	},
})

export const useDeviceStreams = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	const queryClient = useQueryClient()
	const previousPartyAccountId = useRef<string | undefined | null>(null)
	const previousAccessToken = useRef<string | null>(null)

	useEffect(() => {
		// At the start of each effect run, check if partyAccountId changed
		// If it did, clean up the previous subscription first
		if (previousPartyAccountId.current !== null && previousPartyAccountId.current !== partyAccountId) {
			const oldParams: Record<string, string> = {}
			if (previousPartyAccountId.current) {
				oldParams['party-account-id'] = previousPartyAccountId.current
			}
			if (previousAccessToken.current) {
				oldParams.accessToken = previousAccessToken.current
			}
			unsubscribeFromSocket(`${env.OBSERVE_WS}/device-stream`, oldParams)
			unsubscribeFromSocket(`${env.OBSERVE_WS}/device-metric-stream`, oldParams)
		}

		const subscribe = async () => {
			const params: Record<string, string> = {}
			if (partyAccountId) {
				params['party-account-id'] = partyAccountId
			}
			const accessToken = await authManager.getAccessToken()
			params.accessToken = accessToken
			const deviceListener = deviceStreamListener(queryClient, partyAccountId)
			const metricsListener = metricsStreamListener(queryClient, partyAccountId)

			subscribeToSocket(`${env.OBSERVE_WS}/device-stream`, deviceListener, params)
			subscribeToSocket(`${env.OBSERVE_WS}/device-metric-stream`, metricsListener, params)
			previousPartyAccountId.current = partyAccountId
			previousAccessToken.current = params.accessToken || null
		}

		// Only subscribe if partyAccountId actually changed (or it's the first run)
		if (previousPartyAccountId.current !== partyAccountId) {
			subscribe().catch((err) => {
				console.error('Failed to subscribe to device or device metric stream:', err)
			})
		}
	}, [queryClient, partyAccountId])
}

export const useDevices = () => {
	const { data: devices, ...rest } = useDeviceList()
	const { data: deviceRecord } = useDeviceRecord()

	const expandedDevices = useMemo(() => {
		const makeExpandedDevice = (device: Device): ExpandedDevice => {
			const sensors =
				device.deviceRelations
					?.filter((relation) => relation.type === 'Parent' && relation.targetDeviceId !== device.id)
					.map((relation) => deviceRecord?.[relation.targetDeviceId])
					.filter((sensor) => isMeasurementSensor(sensor))
					.map(makeExpandedDevice) ?? []
			return { ...device, sensors }
		}

		return devices?.map(makeExpandedDevice) ?? []
	}, [devices, deviceRecord])

	return { data: expandedDevices, ...rest }
}

export const deviceListQuery = (partyAccountId?: string) =>
	queryOptions({
		queryKey: [...deviceQueryKeys.DEVICES, partyAccountId],
		queryFn: () => {
			return DevicesApi.get('v1/devices', { searchParams: { deviceType: 'GATEWAY', partyAccountId } }).json<Device[]>()
		},
		staleTime: 1000 * 60 * 5,
	})

const useDeviceList = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	return useQuery(deviceListQuery(partyAccountId))
}

export const useRestartDevice = () =>
	useMutation({
		mutationFn: (deviceId: string) => DevicesApi.post(`v1/device-restarts/${deviceId}`),
	})

export const useRegisterDevice = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	const queryClient = useQueryClient()
	return useMutation<KyResponse<unknown>, ErrorWithStatus, DeviceRegistrationRequest>({
		mutationFn: (body: DeviceRegistrationRequest) => DevicesApi.post('v1/device-registrations', { json: { ...body, partyAccountId } }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: [...deviceQueryKeys.DEVICES, partyAccountId] })
			// The backend needs an unknown time to add a new device to the socket streams, so reset more than once
			for (const delay of [5000, 10_000, 15_000]) setTimeout(resetAllSockets, delay)
		},
	})
}

export const useUpdateDevice = () => {
	return useMutation({
		mutationFn: ({ deviceId, body }: { deviceId: string; body: DeviceUpdateRequest }) =>
			DevicesApi.patch(`v1/devices/${deviceId}`, { json: body }),
	})
}

export const useDeleteDevice = ({ beforeReset }: { beforeReset?: () => Promise<unknown> } = {}) => {
	const partyAccountId = useImpersonation()?.partyAccountId
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (deviceId: string) => DevicesApi.post('v1/device-registration-removals', { searchParams: { deviceId, partyAccountId } }),
		onSuccess: async () => {
			await beforeReset?.()
			await queryClient.resetQueries({ queryKey: [...deviceQueryKeys.DEVICES, partyAccountId] })
			setTimeout(() => {
				resetAllSockets()
			}, 5000)
		},
	})
}

export const useDeviceRecord = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	return useQuery({
		queryKey: [...deviceQueryKeys.DEVICE_RECORD, partyAccountId],
		queryFn: () => ({}) as Record<string, Device>,
		staleTime: Infinity,
	})
}

export const waitForDeviceRecord = (
	queryClient: QueryClient,
	partyAccountId?: string,
	options?: { timeoutMs?: number; intervalMs?: number },
) => {
	const { timeoutMs = 10_000, intervalMs = 100 } = options ?? {}
	return new Promise<void>((resolve) => {
		const startTime = Date.now()
		const checkData = () => {
			const deviceRecord = queryClient.getQueryData([...deviceQueryKeys.DEVICE_RECORD, partyAccountId])
			if (deviceRecord && Object.keys(deviceRecord).length > 0) {
				resolve()
				return
			}
			if (Number.isFinite(timeoutMs) && Date.now() - startTime >= timeoutMs) {
				resolve()
				return
			}
			setTimeout(checkData, intervalMs) // Poll at configured interval
		}
		checkData()
	})
}

export const useDeviceActivities = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	return useQuery({
		queryKey: [...deviceQueryKeys.DEVICE_ACTIVITY, partyAccountId],
		queryFn: () => ({}) as Record<string, DeviceActivity>,
		staleTime: Infinity,
	})
}

export const useDeviceStatuses = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	return useQuery({
		queryKey: [...deviceQueryKeys.DEVICE_STATUS, partyAccountId],
		queryFn: () => ({}) as Record<string, DeviceStatus>,
		staleTime: Infinity,
	})
}

export const useDeviceMetrics = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	return useQuery({
		queryKey: [...deviceQueryKeys.DEVICE_METRICS, partyAccountId],
		queryFn: () => ({}) as DeviceMetricRecord,
		staleTime: Infinity,
	})
}

export const searchDevicesQuery = (searchQuery: string) =>
	queryOptions({
		queryKey: ['DEVICE_SEARCH', searchQuery],
		queryFn: () => {
			const searchParams = new URLSearchParams({ query: searchQuery })
			return DevicesApi.get('v1/devices-search', { searchParams }).json<DeviceSearchResponse>()
		},
		enabled: searchQuery.trim().length >= 3,
		staleTime: 1000 * 60 * 5,
	})
export const useSearchDevices = (searchQuery: string) => useQuery(searchDevicesQuery(searchQuery))

export const useSearchDevicesByIds = (ids: string[]) => {
	const uniqueIds = [...new Set(ids)]
	return useQueries({
		queries: uniqueIds.map((id) => searchDevicesQuery(id)),
		combine: (results) => ({
			devices: uniqueIds.flatMap((id, i) => results[i]?.data?.results?.find((d) => d.id === id) ?? []),
			isLoading: results.some((r) => r.isLoading),
		}),
	})
}

const deviceSearchStatusFilters = ['ERROR', 'DEGRADED', 'OFFLINE', 'OK'] as const

const deviceSearchStatusFields = [
	'id',
	'name',
	'statusSummary',
	'locations',
	'lastMeasurementTime',
	'productCode',
	'simOperator',
	'partyAccounts',
	'devices',
] as const satisfies readonly (keyof SearchDevice)[]

const searchDevicesByStatusQuery = (statusFilter: (typeof deviceSearchStatusFilters)[number]) =>
	queryOptions({
		queryKey: ['DEVICE_SEARCH_STATUS', statusFilter],
		queryFn: () => {
			const searchParams = new URLSearchParams({ status_summary_filter: statusFilter, fields: deviceSearchStatusFields.join(',') })
			return DevicesApi.get('v1/devices-search', { searchParams, timeout: 30_000 }).json<
				DeviceSearchResponse<(typeof deviceSearchStatusFields)[number]>
			>()
		},
		staleTime: 1000 * 60 * 5,
	})

// Split into one request per status instead of one big request for all statuses
// To try to make each request faster and not timeout.
export const useSearchDevicesByStatus = () => {
	const combine = useCallback(
		(results: UseQueryResult<DeviceSearchResponse<(typeof deviceSearchStatusFields)[number]>>[]) => ({
			data: {
				results: results.flatMap((r) => r.data?.results ?? []),
				metadata: {
					mainDeviceCount: results.reduce((sum, r) => sum + (r.data?.metadata.mainDeviceCount ?? 0), 0),
					deviceCount: results.reduce((sum, r) => sum + (r.data?.metadata.deviceCount ?? 0), 0),
					allResults: results.every((r) => r.data?.metadata.allResults === true),
				},
			},
			isLoading: results.some((r) => r.isLoading),
			isFetching: results.some((r) => r.isFetching),
			isError: results.some((r) => r.isError),
			error: results.find((r) => r.error)?.error,
		}),
		[],
	)
	return useQueries({
		queries: deviceSearchStatusFilters.map((statusFilter) => searchDevicesByStatusQuery(statusFilter)),
		combine,
	})
}

const nonMeasurementSensors = ['CB', 'EG25', 'PSU', 'DMU808BB', 'DMU802BB', 'DMU804BB']
const isMeasurementSensor = (sensor?: Device): sensor is Device =>
	!!sensor && !nonMeasurementSensors.some((code) => sensor.productCode.includes(code))

const deviceStreamListener = (queryClient: QueryClient, partyAccountId: string | undefined) => (e: MessageEvent) => {
	const message = JSON.parse(e.data) as DeviceStreamMessage
	if (message.payloadType === 'device-statuses') {
		const payload = message.payload
		queryClient.setQueryData([...deviceQueryKeys.DEVICE_STATUS, partyAccountId], (oldData: Record<string, DeviceStatus>) =>
			generateRecordPatch(payload, 'source', oldData),
		)
	} else if (message.payloadType === 'device-activities') {
		const payload = message.payload
		queryClient.setQueryData([...deviceQueryKeys.DEVICE_ACTIVITY, partyAccountId], (oldData: Record<string, DeviceActivity>) =>
			generateRecordPatch(payload, 'deviceId', oldData),
		)
	} else if (message.payloadType === 'device-updates') {
		const payload = message.payload
		queryClient.setQueryData([...deviceQueryKeys.DEVICES, partyAccountId], (oldData: Device[] | undefined) =>
			generateGatewayListPatch(oldData ?? [], payload),
		)
		queryClient.setQueryData([...deviceQueryKeys.DEVICE_RECORD, partyAccountId], (oldData: Record<string, Device>) =>
			generateRecordPatch(payload, 'id', oldData),
		)
	}
}

const metricsStreamListener = (queryClient: QueryClient, partyAccountId: string | undefined) => (e: MessageEvent) => {
	const message = JSON.parse(e.data) as DeviceMetric[]
	queryClient.setQueryData([...deviceQueryKeys.DEVICE_METRICS, partyAccountId], (oldData: DeviceMetricRecord) =>
		generateMetricRecordPatch(oldData, message),
	)
}

const generateGatewayListPatch = (currentData: Device[], newData: Device[]) => {
	const deviceMap = new Map(currentData.map((device) => [device.id, device]))
	for (const device of newData.filter((device) => device.deviceType === 'GATEWAY')) {
		deviceMap.set(device.id, device)
	}
	return Array.from(deviceMap.values())
}

const generateRecordPatch = <T, K extends keyof T>(
	newData: T[],
	key: K & (T[K] extends string | number ? K : never),
	currentData: Record<string, T> = {},
) => {
	const data = structuredClone(currentData)
	for (const item of newData) {
		Object.assign(data, { [item[key] as string]: item })
	}
	return data
}

const generateMetricRecordPatch = (currentData: DeviceMetricRecord, newData: DeviceMetric[]) => {
	const data = structuredClone(currentData) ?? {}
	for (const metric of newData) {
		const dataForSource = data[metric.source] ?? {}
		data[metric.source] = { ...dataForSource, [metric.metric]: metric }
	}
	return data
}

export const getParentDeviceMap = (devices: ExpandedDevice[]) => {
	const map = new Map<string, string>()

	for (const device of devices) {
		for (const sensor of device.sensors) {
			map.set(sensor.id, device.id)
		}
		map.set(device.id, device.id)
	}
	return map
}

type DevicePlaceRelationBase = { activeFrom: string; deviceId: string; id: string }
type KnownDevicePlaceRelation = DevicePlaceRelationBase & {
	place: { type: string; id: string; latitude: number; longitude: number; altitude?: number }
	type: 'Measurement' | 'Installation'
}
type ShipmentPlaceRelation = DevicePlaceRelationBase & { place: unknown; type: 'Shipment' }
type DevicePlaceRelation = KnownDevicePlaceRelation | ShipmentPlaceRelation

export interface Device {
	activeFrom: string
	applicationProtocols?: { id: string; inUse: boolean; type: string; version: string }[]
	description?: string
	deviceCapabilities?: { id: string; key: string; value: boolean }[]
	deviceFieldProperties?: { nameOverride: string; measurementPlaceOverride: string }
	deviceFirmwareVersion?: { activeFrom: string; id: string; firmwareVersion: string }
	devicePlaceRelations?: DevicePlaceRelation[]
	deviceRelations?: { activeFrom: string; id: string; sourceDeviceId: string; targetDeviceId: string; type: string }[]
	deviceSpecifications: []
	deviceType: string
	id: string
	manufacturer: string
	name?: string
	productCode: string
	serialNumber: string
	type: string
}

export type ExpandedDevice = Device & {
	sensors: ExpandedDevice[]
}

export type DeviceStatus = {
	source: string
	id: string
	status: 'OK' | 'OFFLINE' | 'DEGRADED' | 'ERROR' | 'UNKNOWN'
	timestamp: string
	statusDetails: string[] | undefined
}

type DeviceActivity = {
	id: string
	deviceId: string
	connectionTime: string
}

type DeviceStreamMessage =
	| { payloadType: 'device-statuses'; payload: DeviceStatus[] }
	| { payloadType: 'device-activities'; payload: DeviceActivity[] }
	| { payloadType: 'device-updates'; payload: Device[] }

type DeviceSearchResponse<K extends keyof SearchDevice = keyof SearchDevice> = {
	metadata: {
		mainDeviceCount: number
		deviceCount: number
		allResults: boolean
	}
	results: Pick<SearchDevice, K>[]
}

export type SearchDevice = {
	id: string
	name: string
	type: string
	status: DeviceStatus['status']
	statusSummary: DeviceStatus['status']
	productCode?: string
	serialNumber?: string
	firmware?: string
	locations?: {
		latitude: number
		longitude: number
		timestamp: string
	}[]
	partyAccounts?: {
		id: string
		type: string
	}[]
	simIccid?: string
	simOperator?: string
	installationDate?: string
	lastMeasurementTime?: string
	lastActivityTime?: string
	devices?: Omit<SearchDevice, 'devices' | 'statusSummary'>[]
}

const DeviceRegistrationDeviceTypeSchema = z.enum(
	['CONNECTED_COMPACT_BWS', 'GROUND_CAST_SENSOR', 'TEMP_CAST_SENSOR', 'DMU801', 'ATMO_CAST_SENSOR'],
	{
		error: () => i18next.t(($) => $.zod.deviceModelRequired),
	},
)
export type DeviceRegistrationDeviceType = z.infer<typeof DeviceRegistrationDeviceTypeSchema>

const maxLat = 90
const minLat = -90
const maxLon = 180
const minLon = -180
const maxAlt = 9000
const minAlt = -500
const LocationSchema = z.object({
	latitude: z
		.number({
			error: (issue) =>
				issue.input === undefined
					? i18next.t(($) => $.zod.latitudeRequired)
					: i18next.t(($) => $.zod.requiredNumberWithRange, { min: minLat, max: maxLat }),
		})
		.min(minLat, { error: () => i18next.t(($) => $.zod.outOfRange, { min: minLat, max: maxLat }) })
		.max(maxLat, { error: () => i18next.t(($) => $.zod.outOfRange, { min: minLat, max: maxLat }) }),
	longitude: z
		.number({
			error: (issue) =>
				issue.input === undefined
					? i18next.t(($) => $.zod.longitudeRequired)
					: i18next.t(($) => $.zod.requiredNumberWithRange, { min: minLon, max: maxLon }),
		})
		.min(minLon, { error: () => i18next.t(($) => $.zod.outOfRange, { min: minLon, max: maxLon }) })
		.max(maxLon, { error: () => i18next.t(($) => $.zod.outOfRange, { min: minLon, max: maxLon }) }),
	altitude: z
		.number({
			error: (issue) =>
				issue.input === undefined
					? i18next.t(($) => $.zod.altitudeRequired)
					: i18next.t(($) => $.zod.requiredNumberWithRange, { min: minAlt, max: maxAlt }),
		})
		.min(minAlt, { error: () => i18next.t(($) => $.zod.outOfRange, { min: minAlt, max: maxAlt }) })
		.max(maxAlt, { error: () => i18next.t(($) => $.zod.outOfRange, { min: minAlt, max: maxAlt }) }),
})

export const DeviceRegistrationRequestSchema = z.object({
	serialNumber: z
		.string()
		.trim()
		.min(1, { error: () => i18next.t(($) => $.zod.serialNumberRequired) })
		.max(32, { error: () => i18next.t(($) => $.zod.tooManyCharacters, { max: 32 }) }),
	deviceType: DeviceRegistrationDeviceTypeSchema,
	name: z
		.string()
		.max(35, {
			error: (issue) => {
				const charsToRemove = Number(issue.input?.length ?? 0) - Number(issue.maximum)
				return i18next.t(($) => $.zod.tooBigName, { count: charsToRemove, max: issue.maximum })
			},
		})
		.optional(),
	location: LocationSchema,
})
export type DeviceRegistrationRequest = z.infer<typeof DeviceRegistrationRequestSchema>

const DeviceUpdateRequestSchema = DeviceRegistrationRequestSchema.pick({ name: true, location: true }).partial()
type DeviceUpdateRequest = z.infer<typeof DeviceUpdateRequestSchema>

const orderedDeviceStatuses = ['UNKNOWN', 'OFFLINE', 'ERROR', 'DEGRADED', 'OK', undefined] as const

export const useDeviceCombinedStatuses = () => {
	const { data: statuses } = useDeviceStatuses()
	const { data: devices } = useDevices()

	const combinedStatuses = useMemo(() => {
		const deviceStatusMap = {} as Record<string, DeviceStatus | undefined>

		for (const device of devices) {
			// Allow only the main device status to be offline, sensors cannot bring the device to offline
			let worstStatus = statuses?.[device.id]
			for (const sensor of device.sensors) {
				const sensorStatus = statuses?.[sensor.id]
				deviceStatusMap[sensor.id] = sensorStatus
				if (sensorStatus?.status !== 'OFFLINE') {
					worstStatus = getWorseStatus(worstStatus, sensorStatus)
				}
			}
			deviceStatusMap[device.id] = worstStatus
		}

		return deviceStatusMap
	}, [statuses, devices])

	return combinedStatuses
}

const getWorseStatus = (status1: DeviceStatus | undefined, status2: DeviceStatus | undefined) => {
	if (!status1) return status2
	if (!status2) return status1

	const value1 = getDeviceStatusSortValue(status1.status)
	const value2 = getDeviceStatusSortValue(status2.status)

	return value2 < value1 ? status2 : status1
}

const getDeviceStatusSortValue = (status: DeviceStatus['status'] | undefined) => {
	return orderedDeviceStatuses.indexOf(status)
}

const isKnownPlaceRelation = (r: DevicePlaceRelation): r is KnownDevicePlaceRelation =>
	r.type === 'Measurement' || r.type === 'Installation'

export const getDeviceLocation = (device: Device) => {
	const knownRelations = device.devicePlaceRelations?.filter(isKnownPlaceRelation) ?? []
	const location = knownRelations.sort((a, b) => b.activeFrom.localeCompare(a.activeFrom))[0]?.place
	if (!location) return undefined
	const { latitude, longitude, altitude } = location
	return { latitude, longitude, altitude }
}

export const getSearchDeviceLocation = (device: Pick<SearchDevice, 'locations'>) => {
	if (!device.locations?.length) return undefined
	return [...device.locations].sort((a, b) => b.timestamp.localeCompare(a.timestamp))[0]
}

export const getSearchDeviceLastReadTime = (device: Pick<SearchDevice, 'lastMeasurementTime' | 'devices'>) =>
	max([device.lastMeasurementTime, ...(device.devices?.map((d) => d.lastMeasurementTime) ?? [])].filter(isNotNil))
